// ─────────────────────────────────────────────────────────────────────────────
// AMMO.JS CLOTH — Bullet btSoftBody cloth simulation
// Higher fidelity than PBD: self-collision, accurate drape, pressure simulation
// Falls back to PBDClothSim when ammo fails to load
//
// Bullet cloth reference: https://pybullet.org/Bullet/BulletFull/btSoftBody_8h.html
// ─────────────────────────────────────────────────────────────────────────────
import * as THREE from "three";
import type { NormalizedLandmark } from "./pose";
import { POSE_IDX } from "./pose";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
type AmmoLib = any;
let _ammo: AmmoLib | null = null;
let _ammoLoading = false;

export async function loadAmmo(): Promise<AmmoLib | null> {
  if (_ammo) return _ammo;
  if (_ammoLoading) {
    await new Promise<void>(res => {
      const iv = setInterval(() => { if (_ammo || !_ammoLoading) { clearInterval(iv); res(); } }, 50);
    });
    return _ammo;
  }
  _ammoLoading = true;
  try {
    // Load ammo.wasm.js from public folder — sets window.Ammo
    await new Promise<void>((resolve, reject) => {
      const script = document.createElement("script");
      script.src = "/ammo.wasm.js";
      script.onload = () => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        (window as any).Ammo().then((lib: AmmoLib) => {
          _ammo = lib;
          console.log("[Cloth] ammo.js btSoftBody ready ✓");
          resolve();
        }).catch(reject);
      };
      script.onerror = reject;
      document.head.appendChild(script);
    });
  } catch (err) {
    console.warn("[Cloth] ammo.js failed to load — PBD fallback active", err);
  }
  _ammoLoading = false;
  return _ammo;
}

// ── Coordinate conversion: MediaPipe → Bullet world space ────────────────────
function toWorld(x: number, y: number, isMirrored: boolean) {
  const nx = isMirrored ? 1 - x : x;
  return {
    x: (nx - 0.5) * 4,
    y: -(y - 0.5) * 4,
  };
}

// ── ammo.js btSoftBody cloth simulator ───────────────────────────────────────
export class AmmoClothSim {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private Ammo: AmmoLib;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private world: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  private softBody: any;
  private cols: number;
  private rows: number;

  readonly geometry: THREE.BufferGeometry;
  private posAttr: THREE.BufferAttribute;
  private normAttr: THREE.BufferAttribute;

  constructor(Ammo: AmmoLib, cols = 16, rows = 24, width = 0.6, height = 0.8) {
    this.Ammo = Ammo;
    this.cols = cols;
    this.rows = rows;

    // ── Bullet world setup ───────────────────────────────────────────────────
    const broadphase       = new Ammo.btDbvtBroadphase();
    const collisionConfig  = new Ammo.btSoftBodyRigidBodyCollisionConfiguration();
    const dispatcher       = new Ammo.btCollisionDispatcher(collisionConfig);
    const solver           = new Ammo.btSequentialImpulseConstraintSolver();
    const softBodySolver   = new Ammo.btDefaultSoftBodySolver();
    this.world             = new Ammo.btSoftRigidDynamicsWorld(
      dispatcher, broadphase, solver, collisionConfig, softBodySolver
    );
    this.world.setGravity(new Ammo.btVector3(0, -9.8, 0));

    // ── Cloth patch — 4 corners, cols×rows resolution ───────────────────────
    const halfW = width / 2;
    const c00 = new Ammo.btVector3(-halfW,  0,       0);
    const c10 = new Ammo.btVector3( halfW,  0,       0);
    const c01 = new Ammo.btVector3(-halfW, -height,  0);
    const c11 = new Ammo.btVector3( halfW, -height,  0);

    const helpers   = new Ammo.btSoftBodyHelpers();
    this.softBody   = helpers.CreatePatch(
      this.world.getWorldInfo(),
      c00, c10, c01, c11,
      cols, rows,
      0,    // fixedCorners bitmask: 0 = none (we pin manually)
      true  // generateDiagonals
    );

    // ── Cloth material config ────────────────────────────────────────────────
    const cfg = this.softBody.get_m_cfg();
    cfg.set_viterations(10);  // velocity solver iterations
    cfg.set_piterations(10);  // position solver iterations
    cfg.set_collisions(0x11); // SDF_RS | CL_SS — self collision + rigid body
    cfg.set_kDF(0.5);         // dynamic friction
    cfg.set_kDP(0.01);        // damping
    cfg.set_kPR(0);           // pressure (0 = flat cloth, >0 = inflated)

    const mat = this.softBody.get_m_materials().at(0);
    mat.set_m_kLST(0.9);  // linear stiffness
    mat.set_m_kAST(0.9);  // angular stiffness
    mat.set_m_kVST(0.9);  // volume stiffness

    this.softBody.setTotalMass(0.9, false);
    this.softBody.generateBendingConstraints(2);  // skip-1 bending

    this.world.addSoftBody(this.softBody, 1, -1);

    // ── Three.js geometry ────────────────────────────────────────────────────
    const vertCount = cols * rows;
    const positions  = new Float32Array(vertCount * 3);
    const normals    = new Float32Array(vertCount * 3);
    const uvs        = new Float32Array(vertCount * 2);
    const indices: number[] = [];

    for (let r = 0; r < rows; r++) {
      for (let c = 0; c < cols; c++) {
        const i = r * cols + c;
        uvs[i * 2]     = c / (cols - 1);
        uvs[i * 2 + 1] = 1 - r / (rows - 1);
        if (r < rows - 1 && c < cols - 1) {
          const a = i, b = i + 1, d = i + cols, e = i + cols + 1;
          indices.push(a, b, d, b, e, d);
        }
      }
    }

    this.geometry   = new THREE.BufferGeometry();
    this.posAttr    = new THREE.BufferAttribute(positions, 3);
    this.normAttr   = new THREE.BufferAttribute(normals,   3);
    this.posAttr.setUsage(THREE.DynamicDrawUsage);
    this.normAttr.setUsage(THREE.DynamicDrawUsage);
    this.geometry.setAttribute("position", this.posAttr);
    this.geometry.setAttribute("normal",   this.normAttr);
    this.geometry.setAttribute("uv",       new THREE.BufferAttribute(uvs, 2));
    this.geometry.setIndex(indices);
  }

  // ── Step physics world ────────────────────────────────────────────────────
  step(dt: number) {
    this.world.stepSimulation(dt, 10, 1 / 60);
    this._syncGeometry();
  }

  // ── Pin top-row nodes to shoulder landmarks ───────────────────────────────
  pinToSkeleton(landmarks: NormalizedLandmark[], isMirrored: boolean) {
    const ls = landmarks[POSE_IDX.LEFT_SHOULDER];
    const rs = landmarks[POSE_IDX.RIGHT_SHOULDER];
    if (!ls || !rs) return;

    const left  = toWorld(ls.x, ls.y, isMirrored);
    const right = toWorld(rs.x, rs.y, isMirrored);

    const nodes = this.softBody.get_m_nodes();

    // Top row = nodes 0..(cols-1)
    for (let c = 0; c < this.cols; c++) {
      const t = c / (this.cols - 1);
      const wx = THREE.MathUtils.lerp(left.x,  right.x, t);
      const wy = THREE.MathUtils.lerp(left.y,  right.y, t);

      const node = nodes.at(c);
      const pos  = node.get_m_x();
      const vel  = node.get_m_v();
      pos.setX(wx); pos.setY(wy); pos.setZ(0);
      vel.setX(0);  vel.setY(0);  vel.setZ(0);
      node.set_m_im(0); // infinite mass = pinned
    }
  }

  // ── Add rigid body sphere colliders for each body joint ──────────────────
  applyBodyCollision(landmarks: NormalizedLandmark[], isMirrored: boolean) {
    const Ammo = this.Ammo;
    const joints = [
      POSE_IDX.LEFT_SHOULDER, POSE_IDX.RIGHT_SHOULDER,
      POSE_IDX.LEFT_HIP,      POSE_IDX.RIGHT_HIP,
      POSE_IDX.LEFT_ELBOW,    POSE_IDX.RIGHT_ELBOW,
    ];
    for (const ji of joints) {
      const lm = landmarks[ji];
      if (!lm || (lm.visibility ?? 1) < 0.3) continue;
      const { x, y } = toWorld(lm.x, lm.y, isMirrored);
      const transform = new Ammo.btTransform();
      transform.setIdentity();
      transform.getOrigin().setValue(x, y, 0);
      const shape   = new Ammo.btSphereShape(0.12);
      const rbInfo  = new Ammo.btRigidBodyConstructionInfo(0, new Ammo.btDefaultMotionState(transform), shape);
      const rb      = new Ammo.btRigidBody(rbInfo);
      this.world.addRigidBody(rb);
    }
  }

  setFabric(params: { stiffness?: number; damping?: number; mass?: number }) {
    const cfg = this.softBody.get_m_cfg();
    if (params.damping  !== undefined) cfg.set_kDP(params.damping);
    if (params.mass     !== undefined) this.softBody.setTotalMass(params.mass, false);
    if (params.stiffness !== undefined) {
      const mat = this.softBody.get_m_materials().at(0);
      mat.set_m_kLST(params.stiffness);
      mat.set_m_kAST(params.stiffness);
    }
  }

  private _syncGeometry() {
    const Ammo = this.Ammo;
    const nodes = this.softBody.get_m_nodes();
    const pos   = this.posAttr.array as Float32Array;
    const nrm   = this.normAttr.array as Float32Array;

    for (let i = 0; i < this.cols * this.rows; i++) {
      const node = nodes.at(i);
      const p    = node.get_m_x();
      const n    = node.get_m_n();
      pos[i * 3]     = p.x();
      pos[i * 3 + 1] = p.y();
      pos[i * 3 + 2] = p.z();
      nrm[i * 3]     = n.x();
      nrm[i * 3 + 1] = n.y();
      nrm[i * 3 + 2] = n.z();
      Ammo.destroy(p);
      Ammo.destroy(n);
    }

    this.posAttr.needsUpdate = true;
    this.normAttr.needsUpdate = true;
  }

  dispose() {
    this.world.removeSoftBody(this.softBody);
    this.Ammo.destroy(this.softBody);
    this.Ammo.destroy(this.world);
    this.geometry.dispose();
  }
}
